import { newGetInitialValues, newValidate, Translation as T, useAmsSelector, useTranslation } from "@awe-web/shared/lib";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, RequiredNotice } from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";
import { ScreenHeading, ScreenLoading, ScreenSection, ScreenSectionHeading } from "../../../components/screen.js";
import SelectDriverForm from "../../../rent_contracts/components/select_driver_form.jsx";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import { B2B_STR_SelectExtras } from "../../../routing/routes.js";
import SelectedStationAndTime from "../../../stations/components/selected_station_and_time.jsx";
import { useInitialize } from "../../../util/react.js";
import { initialize, submit } from "./actions.js";

import "./select_driver.scss";

const SCREEN_HEADING = "ALG0000065";
const BACK = "ALG0000030";
const DRIVER_FORM_HEADING = "ALG0000066";
const IMAGE = "ALG0000241";
const SUBMIT = "ALG0000072";
const NOTICE = "FSD0000209";
const PRICES_NOTICE = "ALG0000029";

function DriverForm({ handleSubmit }) {
  const { translate } = useTranslation();

  return (
    <form onSubmit={handleSubmit}>
      <Row>
        <Col desktop={{ cols: 8, offset: 2 }}>
          <ScreenHeading><T code={SCREEN_HEADING} /></ScreenHeading>
        </Col>
      </Row>

      <Row className="b2b-str-select-driver__form-row">
        <Col desktop={{ cols: 4, offset: 2 }}>
          <ScreenSectionHeading light>
            <T code={DRIVER_FORM_HEADING} />
          </ScreenSectionHeading>

          <SelectDriverForm className="b2b-str-select-driver__fieldset" />
        </Col>
        <Col className="b2b-str-select-driver__image-col" desktop={{ cols: 4 }}>
          <img src={translate(IMAGE)} alt="" />
        </Col>
      </Row>

      <Row className="b2b-str-select-driver__form-row">
        <Col desktop={{ cols: 4, offset: 6 }}>
          <Button primary submit continue><T code={SUBMIT} /></Button>
        </Col>
      </Row>
    </form>
  );
}

DriverForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

function InternalDriverFormWrapper({ onSubmit }) {
  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("personContractDriver");
    const inialValuesFactory = formInitializer(state);
    return inialValuesFactory({
      "Person:prefixKey": "",
      "Person:name": "",
      "Person:surname": "",
      "Address:email": "",
      "Address:phone": "",
      "employeeId": "",
      "costCenter": ""
    });
  });

  const validateDriver = useAmsSelector((state) => newValidate("personContractDriver")(state));

  return (
    <ScreenSection>
      <TypeArea>
        <Formik initialValues={initialValues} validate={validateDriver} onSubmit={onSubmit}>
          {(formikProps) => (
            <DriverForm {...formikProps} />
          )}
        </Formik>

        <Row>
          <Col desktop={{ cols: 8, offset: 2 }}>
            <RequiredNotice className="b2b-str-select-driver__required-notice">
              <T code={NOTICE} />
            </RequiredNotice>
            <div><T code={PRICES_NOTICE} /></div>
          </Col>
        </Row>
      </TypeArea>
    </ScreenSection>
  );
}

export default function B2B_STR_SelectDriverComponent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSubmit = useCallback(async (formValues) => {
    const nextPath = await dispatch(submit(formValues));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);
  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2b-str-select-driver">
      <ScreenSection dark>
        <TypeArea>
          <SelectedStationAndTime reservationPath="B2B" />
        </TypeArea>
      </ScreenSection>

      <ScreenSection light>
        <TypeArea>
          <SelectedRentObject
            className="b2b-str-select-driver__selected-rent-object"
            selectedBasicProperties="B2B"
            action={(
              <Button secondary back to={B2B_STR_SelectExtras.path()}>
                <T code={BACK} />
              </Button>
            )}
          />
        </TypeArea>
      </ScreenSection>

      <InternalDriverFormWrapper onSubmit={onSubmit} />
    </div>
  );
}
